import {
  AbilityBuilder,
  AbilityClass,
  createAliasResolver,
  PureAbility,
} from "@casl/ability";
import { InjectionKey } from "vue";
import { ABILITY_TOKEN } from "@casl/vue";
import { intersection } from "lodash";

type Actions =
  | "create"
  | "read"
  | "update"
  | "delete"
  | "fullAccess"
  | "modify";

export const subjectsArray = [
  // ---------------------------------- MES -----------------------------------
  "Handheld",
  "Login",
  "MfaSetup",
  "MfaVerification",
  "ChangePassword",
  "ForgotPassword",
  "Verification",
  "Api",
  "Articles",
  "Dashboards",
  "Devices",
  "Factories",
  "Home",
  "Error",
  "Orders",
  "OrderDetails",
  "OrdersQueued",
  "My Profile",
  "Settings",
  "Planning",
  "Reports",
  "StickerTemplates",
  "Tasks",
  "Templates",
  "DefaultPlanning",
  "Tokens",
  "Users",
  "Photos",

  // ------------------------------- ZH factory -------------------------------
  "ZH Cockpits",
  "ZH Factory",
  "ZH PreparePP",
  "ZH Buffer Mabo PP",
  "ZH Dashboard",

  // ------------------------------- ZF factory -------------------------------
  "ZF AR",
  "ZF Articles",
  "ZF Assets",
  "ZF Cockpits",
  "ZF DashboardAssembly",
  "ZF DashboardAssemblyProgress",
  "ZF DashboardProgress",
  "ZF Dashboard Assembly",
  "ZF Factory",
  "ZF Luxe-Line Assembly",
  "ZF Panels",
  "ZF Passdoor Assembly",
  "ZF Powder Coating",
  "ZF PP",
  "ZF PreparePP",
  "ZF PreparePaintPP",
  "ZF Saw infeed",
  "ZF Saws optimizer",
  "ZF Settings",
  "ZF Spider",
  "ZF Tasks",

  // ---------------------------------- ZF Handheld -----------------------------------
  "ZF Handheld",
  "ZF Handheld Assembly Rubber",
  "ZF Handheld Assembly Stickers",
  "ZF Handheld Assembly EndCap",
  "ZF Handheld Assembly Bottom",
  "ZF Handheld Assembly Top",
  "ZF Handheld Assembly Special1",
  "ZF Handheld Assembly SpecialsOverview",
  "ZF Handheld Assembly SpecialsDetail",
  "ZF Handheld Assembly Special2",
  "ZF Handheld Assembly Collo",

  "ZF Handheld Stations Lintel PreparePaintPP",
  "ZF Handheld Stations Lintel PreparePP",

  // ---------------------------------- HF Handheld -----------------------------------
  "ZH Handheld Stations Mabo BufferPP",
  "ZH Handheld Infeed1",
  "ZH Handheld Infeed2",
] as const;

export type Subjects = (typeof subjectsArray)[number];
export type AppAbility = PureAbility<[Actions, Subjects]>;
export const AppAbility = PureAbility as AbilityClass<AppAbility>;

export function getSubject(subject: Subjects): string {
  return subject;
}

export function getSubjectOrDefault(subject: string): Subjects {
  if (subjectsArray.findIndex((o) => o == subject) < 0) {
    return "Home";
  } else {
    return subject as Subjects;
  }
}

export function getSubjectFromString(
  subject: string | undefined,
): Subjects | undefined {
  if (subject == undefined) {
    return undefined;
  } else if (subjectsArray.findIndex((o) => o == subject) < 0) {
    return undefined;
  } else {
    return subject as Subjects;
  }
}

const resolveAction = createAliasResolver({
  fullAccess: ["create", "read", "update", "delete"],
  modify: ["read", "update"],
});

/*
  Should these be retrieved using the GqlGroups query?
  (i.e. after that query is updated to not return unused groups)
export const UserGroupOptions = [
  'Admin', 
  'Dashboard', 
  'Guest', 
  'MainOperator', 
  'Operator', 
  'Superuser'
] as const;

export type UserGroup = typeof UserGroupOptions[number];
*/

export default function defineRules(groups: string[]) {
  const { can, rules } = new AbilityBuilder(AppAbility);

  if (intersection(groups, ["Superuser"]).length > 0) {
    // ---------------------------------- MES -----------------------------------
    can("fullAccess", "Tokens");
    can("fullAccess", "Photos");
  }

  if (intersection(groups, ["Superuser", "Admin"]).length > 0) {
    // ---------------------------------- MES -----------------------------------
    can("fullAccess", "Api");
    can("fullAccess", "Articles");
    can("fullAccess", "Dashboards");
    can("fullAccess", "Devices");
    can("fullAccess", "Factories");
    can("fullAccess", "Home");
    can("fullAccess", "Orders");
    can("fullAccess", "OrderDetails");
    can("fullAccess", "Planning");
    can("fullAccess", "Reports");
    can("fullAccess", "Settings");
    can("fullAccess", "StickerTemplates");
    can("fullAccess", "Tasks");
    can("fullAccess", "Templates");
    can("fullAccess", "DefaultPlanning");
    can("fullAccess", "Users");

    // ------------------------------- ZH factory -------------------------------
    can("fullAccess", "ZH Cockpits");
    can("fullAccess", "ZH Factory");
    can("fullAccess", "ZH PreparePP");

    // ------------------------------- ZF factory -------------------------------
    can("fullAccess", "ZF AR");
    can("fullAccess", "ZF Articles");
    can("fullAccess", "ZF Assets");
    can("fullAccess", "ZF Cockpits");
    can("fullAccess", "ZF DashboardAssembly");
    can("fullAccess", "ZF DashboardAssemblyProgress");
    can("fullAccess", "ZF DashboardProgress");
    can("fullAccess", "ZF Factory");
    can("fullAccess", "ZF Luxe-Line Assembly");
    can("fullAccess", "ZF Panels");
    can("fullAccess", "ZF Passdoor Assembly");
    can("fullAccess", "ZF Powder Coating");
    can("fullAccess", "ZF PP");
    can("fullAccess", "ZF PreparePP");
    can("fullAccess", "ZF PreparePaintPP");
    can("fullAccess", "ZF Saw infeed");
    can("fullAccess", "ZF Saws optimizer");
    can("fullAccess", "ZF Settings");
    can("fullAccess", "ZF Spider");
    can("fullAccess", "ZF Tasks");

    // -------------------------------- ZF Handheld -----------------------------
    can("fullAccess", "ZF Handheld");
    can("fullAccess", "ZF Handheld Assembly Rubber");
    can("fullAccess", "ZF Handheld Assembly Stickers");
    can("fullAccess", "ZF Handheld Assembly EndCap");
    can("fullAccess", "ZF Handheld Assembly Bottom");
    can("fullAccess", "ZF Handheld Assembly Top");
    can("fullAccess", "ZF Handheld Assembly Special1");
    can("fullAccess", "ZF Handheld Assembly SpecialsOverview");
    can("fullAccess", "ZF Handheld Assembly SpecialsDetail");
    can("fullAccess", "ZF Handheld Assembly Special2");
    can("fullAccess", "ZF Handheld Assembly Collo");

    can("fullAccess", "ZF Handheld Stations Lintel PreparePaintPP");
    can("fullAccess", "ZF Handheld Stations Lintel PreparePP");

    // -------------------------------- ZH Handheld -----------------------------
    can("fullAccess", "ZH Handheld Stations Mabo BufferPP");
    can("fullAccess", "ZH Handheld Infeed1");
    can("fullAccess", "ZH Handheld Infeed2");
  }

  if (intersection(groups, ["Operator"]).length > 0) {
    // ---------------------------------- MES -----------------------------------
    can("read", "Articles");
    can("read", "Devices");
    can("read", "Factories");
    can("read", "Home");
    can("read", "OrderDetails");
    can("read", "Orders");
    can("read", "Planning");
    can("read", "Settings");
    can("read", "Tasks");

    // ------------------------------- ZH factory -------------------------------
    can("read", "ZH Cockpits");
    can("read", "ZH Factory");
    can("modify", "ZH PreparePP");

    // ------------------------------- ZF factory -------------------------------
    can("modify", "ZF AR");
    can("modify", "ZF Articles");
    can("read", "ZF Assets");
    can("read", "ZF Cockpits");
    can("read", "ZF Factory");
    can("read", "ZF Panels");
    can("modify", "ZF PreparePP");
    can("modify", "ZF Saws optimizer");
    can("modify", "ZF Saw infeed");
    can("read", "ZF Settings");
    can("read", "ZF Spider");
    can("modify", "ZF Tasks");

    // -------------------------------- Handheld --------------------------------
    can("read", "ZF Handheld");
    can("fullAccess", "ZF Handheld Assembly Rubber");
    can("fullAccess", "ZF Handheld Assembly Stickers");
    can("fullAccess", "ZF Handheld Assembly EndCap");
    can("fullAccess", "ZF Handheld Assembly Bottom");
    can("fullAccess", "ZF Handheld Assembly Top");
    can("fullAccess", "ZF Handheld Assembly Special1");
    can("fullAccess", "ZF Handheld Assembly SpecialsOverview");
    can("fullAccess", "ZF Handheld Assembly SpecialsDetail");
    can("fullAccess", "ZF Handheld Assembly Special2");
    can("fullAccess", "ZF Handheld Assembly Collo");

    can("modify", "ZF Handheld Stations Lintel PreparePaintPP");
    can("modify", "ZF Handheld Stations Lintel PreparePP");
  }

  if (intersection(groups, ["Sales"]).length > 0) {
    can("read", "Photos");
  }

  if (intersection(groups, ["Dashboard"]).length > 0) {
    // ---------------------------------- MES -----------------------------------

    // ------------------------------- ZH factory -------------------------------

    // ------------------------------- ZF factory -------------------------------
    can("read", "ZF Factory");
    can("read", "ZF Cockpits");
    can("read", "ZF Dashboard Assembly");
  }

  return rules;
}

export function buildAbility(groups: string[]) {
  return new AppAbility(defineRules(groups), { resolveAction });
}
export const TOKEN = ABILITY_TOKEN as InjectionKey<AppAbility>;
